<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Lucky Wheels</h3>
              <v-btn
                v-if="can('LUCKY_WHEELS.CREATE')"
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                @click="openCreateDialog"
              >
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search wheels..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Wheels Grid -->
      <v-row v-else>
        <v-col
          v-for="(wheel, index) in wheels"
          :key="`wheel_${index}`"
          md="6"
          lg="4"
        >
          <v-card elevation="6" rounded="xxl" class="wheel-card">
            <v-card-title class="primary white--text">
              <v-icon class="mr-2" color="white">mdi-ferris-wheel</v-icon>
              <h4>{{ wheel.name }}</h4>
              <v-spacer></v-spacer>
              <v-chip
                small
                :color="wheel.is_active ? 'success' : 'error'"
                text-color="white"
              >
                {{ wheel.is_active ? "Active" : "Inactive" }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-4">
              <div class="mb-3">
                <p class="text--secondary mb-1">{{ wheel.description }}</p>
              </div>

              <v-divider class="my-3"></v-divider>

              <v-row dense>
                <v-col cols="6">
                  <div class="text-center">
                    <v-icon color="amber">mdi-coin</v-icon>
                    <div class="text-h6 font-weight-bold">
                      {{ wheel.spin_cost }}
                    </div>
                    <div class="text-caption text--secondary">1 Spin</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="text-center">
                    <v-icon color="amber">mdi-gold</v-icon>
                    <div class="text-h6 font-weight-bold">
                      {{ wheel.ten_spin_cost }}
                    </div>
                    <div class="text-caption text--secondary">10 Spins</div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <div class="text-center">
                <v-chip small outlined color="primary">
                  <v-icon small left>mdi-gift</v-icon>
                  {{ wheel.items ? wheel.items.length : 0 }} Rewards
                </v-chip>
              </div>
            </v-card-text>

            <v-card-actions class="justify-center pb-4">
              <v-btn
                v-if="can('LUCKY_WHEELS.UPDATE')"
                small
                color="primary"
                outlined
                @click="openEditDialog(wheel)"
              >
                <v-icon small left>mdi-pencil</v-icon>
                Edit
              </v-btn>

              <v-btn
                v-if="can('LUCKY_WHEELS.VIEW')"
                small
                color="info"
                outlined
                @click="viewItems(wheel)"
              >
                <v-icon small left>mdi-eye</v-icon>
                Rewards
              </v-btn>

              <v-btn
                v-if="can('LUCKY_WHEELS.DELETE')"
                small
                color="error"
                outlined
                @click="confirmDelete(wheel)"
              >
                <v-icon small left>mdi-delete</v-icon>
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <v-row v-if="!isLoading && wheels.length > 0">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-pagination
                v-model="page"
                :length="totalPages"
                :total-visible="7"
                @input="getData"
                color="primary"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Create/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="primary white--text">
            <span class="text-h6"
              >{{ editMode ? "Edit" : "Create" }} Lucky Wheel</span
            >
          </v-card-title>
          <v-card-text class="pt-4">
            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model="formData.name"
                label="Wheel Name *"
                :rules="[rules.required]"
                outlined
                dense
                prepend-inner-icon="mdi-ferris-wheel"
              ></v-text-field>

              <v-textarea
                v-model="formData.description"
                label="Description"
                outlined
                dense
                rows="3"
                prepend-inner-icon="mdi-text"
              ></v-textarea>

              <v-text-field
                v-model="formData.image_url"
                label="Image URL"
                outlined
                dense
                prepend-inner-icon="mdi-image"
              ></v-text-field>

              <v-text-field
                v-model.number="formData.spin_cost"
                label="Single Spin Cost *"
                type="number"
                :rules="[rules.required, rules.number]"
                outlined
                dense
                prepend-inner-icon="mdi-coin"
                suffix="coins"
              ></v-text-field>

              <v-text-field
                v-model.number="formData.ten_spin_cost"
                label="10 Spins Cost *"
                type="number"
                :rules="[rules.required, rules.number]"
                outlined
                dense
                prepend-inner-icon="mdi-gold"
                suffix="coins"
              ></v-text-field>

              <v-switch
                v-model="formData.is_active"
                label="Active"
                color="primary"
                inset
              ></v-switch>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Cancel</v-btn>
            <v-btn color="primary" :loading="saving" @click="saveWheel">
              {{ editMode ? "Update" : "Create" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title class="error white--text">
            <v-icon color="white" left>mdi-alert</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="pt-4">
            Are you sure you want to delete this wheel? This will also delete
            all associated rewards and spin records.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" @click="deleteWheel">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "LuckyWheelsPage",
  data() {
    return {
      isLoading: false,
      wheels: [],
      page: 1,
      totalPages: 1,
      filter: {
        search_keyword: null,
        is_active: null,
      },
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      dialog: false,
      deleteDialog: false,
      editMode: false,
      valid: false,
      saving: false,
      deleting: false,
      formData: {
        id: null,
        name: "",
        description: "",
        image_url: "",
        spin_cost: 99,
        ten_spin_cost: 990,
        is_active: true,
      },
      selectedWheel: null,
      rules: {
        required: (v) => !!v || "This field is required",
        number: (v) => v >= 0 || "Must be a positive number",
      },
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          ...this.filter,
        };

        const response = await axios.get("/lucky-wheels", { params });

        if (response.data.success) {
          this.wheels = response.data.data;
          const count = response.data.count || 0;
          this.totalPages = Math.ceil(count / this.rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching wheels:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.editMode = false;
      this.formData = {
        id: null,
        name: "",
        description: "",
        image_url: "",
        spin_cost: 99,
        ten_spin_cost: 990,
        is_active: true,
      };
      this.dialog = true;
    },
    openEditDialog(wheel) {
      this.editMode = true;
      this.formData = {
        id: wheel.id,
        name: wheel.name,
        description: wheel.description,
        image_url: wheel.image_url,
        spin_cost: wheel.spin_cost,
        ten_spin_cost: wheel.ten_spin_cost,
        is_active: wheel.is_active,
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
    },
    async saveWheel() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        let response;
        if (this.editMode) {
          response = await axios.patch(
            `/lucky-wheels/${this.encryptIt(this.formData.id)}`,
            this.formData
          );
        } else {
          response = await axios.post("/lucky-wheels", this.formData);
        }

        if (response.data.success) {
          this.closeDialog();
          this.getData();
        }
      } catch (error) {
        console.error("Error saving wheel:", error);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(wheel) {
      this.selectedWheel = wheel;
      this.deleteDialog = true;
    },
    async deleteWheel() {
      this.deleting = true;
      try {
        const response = await axios.delete(
          `/lucky-wheels/${this.encryptIt(this.selectedWheel.id)}`
        );

        if (response.data.success) {
          this.deleteDialog = false;
          this.getData();
        }
      } catch (error) {
        console.error("Error deleting wheel:", error);
      } finally {
        this.deleting = false;
      }
    },
    viewItems(wheel) {
      this.$router.push(`/lucky-wheels/${this.encryptIt(wheel.id)}/items`);
    },
  },
};
</script>

<style scoped>
.wheel-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
